import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, MessageCircle } from "lucide-react";
import { useSettings, waLink, openChat, scrollToId, EASE } from "../lib/site";
import { WhatsAppIcon } from "./Icons";

const LINES = [
  <>Cura e precisione</>,
  <>per sopracciglia</>,
  <><em className="gold-text pr-2 italic">da sogno</em>e unghie</>,
  <>impeccabili.</>,
];

const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.1em]">
    <motion.span className="block" initial={{ y: "115%" }} animate={{ y: "0%" }} transition={{ duration: 1.2, delay: 0.2 + i * 0.14, ease: EASE }}>
      {children}
    </motion.span>
  </span>
);

const Badge = () => (
  <div className="pointer-events-none absolute -left-10 bottom-16 z-20 h-32 w-32 md:-left-14 md:h-36 md:w-36">
    <svg viewBox="0 0 200 200" className="animate-spin-slow h-full w-full">
      <defs><path id="circ" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" /></defs>
      <circle cx="100" cy="100" r="98" fill="var(--cream)" />
      <text fontSize="15.5" letterSpacing="5" fill="var(--ink)" fontFamily="Hanken Grotesk"><textPath href="#circ">MICROBLADING · NAILS · FRASCATI · </textPath></text>
    </svg>
    <img src="/img/logo-wordmark-dark.png" alt="" data-testid="hero-badge-logo" className="absolute inset-0 m-auto w-[52%]" />
  </div>
);

const HeroVisual = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 110, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 110, damping: 16 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }} className="relative mx-auto w-full max-w-[460px] [perspective:1200px]">
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative [transform-style:preserve-3d]">
        <motion.div initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 28px 28px)" }} animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 28px 28px)" }} transition={{ duration: 1.6, delay: 0.35, ease: [0.76, 0, 0.24, 1] }} className="relative aspect-[4/5] overflow-hidden bg-[var(--blush)]">
          <motion.img style={{ y: imgY, scale: 1.15 }} src="/img/hero-aura.jpg" alt="Donna con microblading e ricostruzione unghie AURA" data-testid="hero-image" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/35 via-transparent to-transparent" />
        </motion.div>
        <motion.div style={{ translateZ: 60 }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4, duration: 0.8, ease: EASE }} className="absolute -right-3 top-16 rounded-2xl border border-white/50 bg-white/55 px-5 py-4 shadow-[0_20px_50px_rgba(42,30,27,.12)] backdrop-blur-xl md:-right-10">
          <p className="eyebrow !text-[10px]">Tecnica</p>
          <p className="font-display text-lg italic">pelo a pelo</p>
        </motion.div>
        <motion.div style={{ translateZ: 90 }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.6, duration: 0.8, ease: EASE }} className="absolute -bottom-6 right-6 rounded-2xl bg-[var(--ink)] px-5 py-4 text-[var(--cream)] shadow-2xl">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[var(--nude)]">Gel · Acrygel</p>
          <p className="font-display text-lg italic">Ricostruzione</p>
        </motion.div>
      </motion.div>
      <Badge />
    </div>
  );
};

export const Hero = () => {
  const s = useSettings();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const wordX = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative overflow-hidden pb-20 pt-32 md:pt-40 lg:min-h-[100svh]">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[620px] w-[620px] rounded-full bg-[radial-gradient(circle,rgba(238,217,208,.95),transparent_65%)]" />
      <div className="relative mx-auto grid max-w-[1400px] items-center gap-16 px-5 md:px-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 1 }} className="mb-8 flex items-center gap-4">
            <span className="h-px w-10 bg-[var(--gold)]" />
            <span className="eyebrow">Atelier di bellezza · Frascati</span>
          </motion.div>
          <h1 data-testid="hero-title" className="font-display text-[2.5rem] leading-[1] tracking-[-0.02em] sm:text-6xl lg:text-[4.6rem]">
            {LINES.map((l, i) => <MaskLine key={i} i={i}>{l}</MaskLine>)}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9, ease: EASE }} className="mt-8 max-w-lg text-base leading-relaxed text-[var(--ink-soft)] md:text-lg">
            Microblading e ricostruzione unghie in un unico atelier. Ogni trattamento parte dal tuo viso, dalle tue mani, dal tuo stile.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.9, ease: EASE }} className="mt-10 flex flex-wrap items-center gap-4">
            <button data-testid="hero-chat-button" onClick={openChat} className="group flex items-center gap-3 rounded-full bg-[var(--ink)] py-4 pl-7 pr-4 text-sm text-[var(--cream)] transition-colors duration-300 hover:bg-[var(--gold-deep)]">
              Prenota in chat
              <span className="grid h-8 w-8 place-items-center rounded-full bg-[var(--cream)] text-[var(--ink)] transition-transform duration-500 group-hover:rotate-[-45deg]"><MessageCircle size={15} /></span>
            </button>
            <a data-testid="hero-whatsapp-button" href={waLink(s)} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-full border border-[var(--ink)]/25 px-6 py-4 text-sm transition-colors duration-300 hover:border-[var(--gold)] hover:text-[var(--gold-deep)]">
              <WhatsAppIcon className="h-4 w-4" /> WhatsApp
            </a>
            <button data-testid="hero-discover-button" onClick={() => scrollToId("microblading")} className="flex items-center gap-2 px-2 py-4 text-sm text-[var(--ink-soft)] hover:text-[var(--ink)]">
              Scopri <ArrowDownRight size={16} />
            </button>
          </motion.div>
        </div>
        <div className="lg:col-span-5"><HeroVisual /></div>
      </div>
      <motion.div style={{ x: wordX }} aria-hidden className="pointer-events-none mt-16 select-none whitespace-nowrap font-display text-[26vw] leading-none tracking-[0.06em] text-stroke opacity-60 lg:-mt-6 lg:text-[18vw]">
        AURA ATELIER
      </motion.div>
    </section>
  );
};
